<template>
  <div v-bind='$attrs'>
    <label
      v-if='label'
      class='block text-sm font-medium'
      :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700"]'
    >
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <div v-if='editMode'>
      <div class='relative mt-1'>
        <fieldset class='mt-2'>
          <legend class='sr-only'>{{ label }}</legend>
          <div class='space-y-4'>
            <div
              v-for='option in field.values'
              :key='option.id'
              class='flex items-center'
            >
              <input
                :id='`${name}_${option.key}`'
                :name='name'
                :value='`option.key`'
                :checked='option.key === modelValue'
                :disabled='disabled'
                type='radio'
                data-cy='radio-field-input'
                class='h-4 w-4 border-gray-300 text-green-600 focus:ring-gray-500'
                @click='selectThis(option.key)'
                @keydown.enter='selectThis(option.key)'
              >
              <label
                :for='`${name}_${option.key}`'
                class='ml-3 block text-sm font-medium'
                :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700"]'
              >
                {{ option.label }}
              </label>
            </div>
          </div>
        </fieldset>
      </div>
      <InputError :error='error'/>
    </div>
    <div v-else>
      <div
        class='flex flex-row items-center gap-x-2'
        data-cy='preview-value'
      >
        <StaticListItem
          v-for='option in field.values'
          :key='option.id'
          :label='option.label'
          :checked='modelValue === option.key'
          :negative='isNegativeOption(option.key)'
        />
      </div>

    </div>
  </div>
  <Modal
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import useInputFieldBase, { InputFieldProps, InputFieldComponents, InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps, InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import StaticListItem from '../base/StaticListItem.vue'

  export default defineComponent({
    name: 'RadioField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      StaticListItem,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
    },
    emits: [
      ...InputFieldEmits,
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, getLabel } = useInputFieldBase()

      function selectThis(value: string) {
        if(props.disabled) {
          return
        }
        updateValue(value)
      }

      function isNegativeOption(key: unknown): boolean {
        return ['0', 'false', 'no'].includes(String(key).trim().toLowerCase())
      }

      return {
        helpVisible, setHelpModalVisibility,  selectThis, getLabel, isNegativeOption,
      }
    },
  })
</script>
